import React, { FC, useEffect, useRef } from 'react';
import styled from 'styled-components';
import Handlebars from 'handlebars';
import DOMPurify from 'dompurify';
import { Block } from 'apps/ng/src/app/services';
import { registerHandlebarsHelpers } from 'apps/ng/src/app/helpers/handlebars.helpers';

interface BlockPreviewProps {
  blocks: Block[];
  htmlCode: string;
  cssCode: string;
  jsCode: string;
  variables?: string;
  context?: any;
}

const StyledPreviewContainer = styled.div`
  width: 100%;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  overflow: hidden;
  background: white;
`;

const StyledPreviewFrame = styled.iframe`
  width: 100%;
  height: 100%;
  border: none;
  background: white;
`;

const StyledPreviewHeader = styled.div`
  padding: 8px 12px;
  background: #f5f5f5;
  border-bottom: 1px solid #e0e0e0;
  font-size: 12px;
  font-weight: 500;
  color: #666666;
`;

export const BlockPreview: FC<BlockPreviewProps> = ({ blocks, htmlCode, cssCode, jsCode, variables, context = {} }) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);

  // Register common Handlebars helpers and partials
  useEffect(() => {
    // Register standard helpers
    registerHandlebarsHelpers();

    // @todo
    Handlebars.registerPartial('@partial-block', '<div></div>');

    for (const b of blocks) {
      Handlebars.registerPartial(b.name, `${b.html || ''} \n<style>${b.blockStylesheet?.css || ''}</style>`);
    }
  }, []);

  useEffect(() => {
    if (!iframeRef.current) return;

    const iframe = iframeRef.current;
    const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;

    if (!iframeDoc) return;

    try {
      // Compile the HTML template with Handlebars
      let compiledHtml = htmlCode;

      if (htmlCode.includes('{{')) {
        const template = Handlebars.compile(htmlCode);

        let parsedVariables: any = {};
        try {
          if (variables) {
            parsedVariables = JSON.parse(variables);
          }
        } catch (error) {
          console.error('Error parsing variables JSON:', error);
        }

        const templateContext = {
          ...parsedVariables.Context,
          ...context,
        };

        compiledHtml = DOMPurify.sanitize(
          template(templateContext, {
            data: {
              System: parsedVariables.System || {},
              Project: parsedVariables.Project || {},
            },
          }),
        );
      }

      // Create the complete HTML document
      const fullHtml = `
        <!DOCTYPE html>
        <html lang="en">
        <head>
          <meta charset="UTF-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          <title>Block Preview</title>
          <style>
            body {
              margin: 0;
              padding: 16px;
              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
              background: white;
            }
            ${cssCode}
          </style>
        </head>
        <body>
          ${compiledHtml}
          <script>
            try {
              ${jsCode}
            } catch (error) {
              console.error('JavaScript execution error:', error);
            }
          </script>
        </body>
        </html>
      `;

      // Write the content to the iframe
      iframeDoc.open();
      iframeDoc.write(fullHtml);
      iframeDoc.close();
    } catch (error) {
      console.error('Error compiling Handlebars template:', error);

      // Fallback to raw HTML if Handlebars compilation fails
      const fallbackHtml = `
        <!DOCTYPE html>
        <html lang="en">
        <head>
          <meta charset="UTF-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          <title>Block Preview</title>
          <style>
            body {
              margin: 0;
              padding: 16px;
              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
              background: white;
            }
            ${cssCode}
          </style>
        </head>
        <body>
          <div style="color: red; padding: 10px; border: 1px solid red; background: #ffe6e6;">
            Error compiling template: ${error}
          </div>
          ${htmlCode}
        </body>
        </html>
      `;

      iframeDoc.open();
      iframeDoc.write(fallbackHtml);
      iframeDoc.close();
    }
  }, [htmlCode, cssCode, jsCode, variables, context]);

  return (
    <StyledPreviewContainer>
      <StyledPreviewHeader>Preview</StyledPreviewHeader>
      <StyledPreviewFrame ref={iframeRef} title="Block Preview" sandbox="allow-scripts allow-same-origin" />
    </StyledPreviewContainer>
  );
};
